import { useState } from 'react';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useEmployeeFeriasHooks } from './hooks';
import { EmployeeFeriasFormDialog } from './EmployeeFeriasFormDialog';
import type { EmployeeFeriasDto, EmployeeFeriasListItem } from './types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'dataInicio',
  sortOrder: 'desc',
};

export interface EmployeeFeriasManagerProps {
  employeeId: number;
}

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

function diffDays(inicio: string, fim: string): number {
  const a = dayjs(inicio);
  const b = dayjs(fim);
  if (!a.isValid() || !b.isValid()) return 0;
  return b.diff(a, 'day') + 1; // inclusivo
}

export function EmployeeFeriasManager({ employeeId }: EmployeeFeriasManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('colaborador-ferias', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<EmployeeFeriasDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<EmployeeFeriasListItem | null>(null);

  const hooks = useEmployeeFeriasHooks(employeeId);
  const listQuery = hooks.useList(query);
  const deleteMut = hooks.useDelete();

  async function openEdit(row: EmployeeFeriasListItem): Promise<void> {
    try {
      setEditing(await hooks.api.get(row.dataInicio));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMut.mutateAsync(pendingDelete.dataInicio);
      toast.success(t('features.colaboradores.ferias.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<EmployeeFeriasListItem>[] = [
    {
      field: 'dataInicio',
      headerName: t('features.colaboradores.ferias.columns.dataInicio'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'dataFim',
      headerName: t('features.colaboradores.ferias.columns.dataFim'),
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'dias',
      headerName: t('features.colaboradores.ferias.columns.dias'),
      width: 90,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => diffDays(params.row.dataInicio, params.row.dataFim),
    },
    {
      field: 'observacao',
      headerName: t('features.colaboradores.ferias.columns.observacao'),
      flex: 1,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.colaboradores.ferias.actions.edit', {
                data: formatDate(params.row.dataInicio),
              })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.colaboradores.ferias.actions.delete', {
                data: formatDate(params.row.dataInicio),
              })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
          {t('features.colaboradores.ferias.actions.new')}
        </Button>
      </Stack>

      <DataTable<EmployeeFeriasListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.colaboradores.ferias.searchPlaceholder')}
      />

      <EmployeeFeriasFormDialog
        open={editing !== null}
        ferias={editing === 'new' ? null : editing}
        hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.colaboradores.ferias.deleteConfirm.title')}
        description={t('features.colaboradores.ferias.deleteConfirm.description', {
          inicio: pendingDelete ? formatDate(pendingDelete.dataInicio) : '',
          fim: pendingDelete ? formatDate(pendingDelete.dataFim) : '',
        })}
        confirmLabel={t('common.delete')}
        loading={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Stack>
  );
}
